/**
 * Local development auth bypass.
 *
 * Enabled only when BOTH are true:
 *   - the app runs under `vite dev` (production builds never bypass), and
 *   - `VITE_AUTH_BYPASS=true` is set in `.env`.
 *
 * When enabled, a fake admin session is written to the same storage keys the
 * real login writes, so the rest of the app (route guard, sidebar, permission
 * checks) works unchanged. To return to real auth, remove the env flag. To
 * remove the bypass entirely, delete this file and its `isAuthBypassEnabled`
 * call sites (ProtectedRoute, Login, axiosClient).
 */
import { PERMISSION_CODES } from "@/constants/permissions";
import { ROLE_CODES } from "@/constants/roles.constant";
import { setStoredUserInfo, setStoredUserPermissions } from "@/lib/auth-storage";

export function isAuthBypassEnabled(): boolean {
  return (
    import.meta.env.DEV &&
    String(import.meta.env.VITE_AUTH_BYPASS ?? "").trim().toLowerCase() ===
      "true"
  );
}

const DEV_USER_INFO = {
  name: "Dev User",
  username: "dev@localhost",
  roleCode: ROLE_CODES.LOYYAL_ADMIN,
  userId: 0,
  roleId: 0,
  programIds: [],
  programCodes: [],
  applicationAccess: [
    "PERK_POINT",
    "ACCESS_POINT",
    "XPAND_POINT",
    "REWARD_POINT",
    "GIFT_CARD",
    "OFFER_POINT",
    "GAMIFICATION",
  ],
};

const PERMISSION_TYPES = ["CREATE", "UPDATE", "VIEW", "DELETE"] as const;

function devPermissions() {
  return Object.values(PERMISSION_CODES).map((code, index) => {
    const type =
      PERMISSION_TYPES.find((prefix) => code.startsWith(`${prefix}_`)) ??
      "VIEW";
    const featureCode = code.startsWith(`${type}_`)
      ? code.slice(type.length + 1)
      : code;
    return {
      id: index + 1,
      permissionCode: code,
      permissionName: code,
      permissionType: type,
      featureVO: { id: index + 1, featureCode, featureName: featureCode },
    };
  });
}

/** Writes the fake session if none exists. Returns true when bypass is on. */
export function ensureDevSession(): boolean {
  if (!isAuthBypassEnabled()) return false;
  if (!sessionStorage.getItem("userInfo") || !localStorage.getItem("userInfo")) {
    setStoredUserInfo(DEV_USER_INFO);
    setStoredUserPermissions(devPermissions());
    localStorage.setItem("isAuthenticated", "true");
  }
  return true;
}
